/**
 * VideoRenewalPage: /video-renewal 라우트 전용 회의 메인 페이지 컨테이너.
 *
 * v1 원본: pages/RenewalVideo/VideoContainer.js (PureComponent, 133+ handlers)
 * v2 변환: Functional + Hooks, TypeScript
 *
 * /video 라우트의 VideoPage.tsx와 동일한 역할이나 다음이 다르다:
 * - Fabric.js 스케치/문서공유 핸들러 없음 (Excalidraw 화이트보드 사용)
 * - screenMode (GRID/SIDE/PRESENTER) 사용
 * - v1의 WrapHOC 패턴 (CSS 클래스 동적 설정) 적용
 *
 * 핸들러는 카테고리별로 단계적 이식:
 * - Phase 1: 미디어 트랙(6) + 사용자 관리(9) + 룸 상태(5) + 연결(4) = 24개
 * - Phase 3~11: 나머지 109개
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { isRecorderMode } from '@/utils/recorderMode';
import { localActions, type LocalState } from '@/store/slices/localSlice';
import { conferenceActions } from '@/store/slices/conferenceSlice';
import { uiActions } from '@/store/slices/uiSlice';
import { participantsActions } from '@/store/slices/participantsSlice';
import { mainUserActions } from '@/store/slices/mainUserSlice';
import { masterActions, type MasterUser } from '@/store/slices/masterSlice';
import { emojiActions } from '@/store/slices/emojiSlice';
import { voiceNoteActions } from '@/store/slices/voiceNoteSlice';
import ConferenceManager from '@/conference/ConferenceManager';
import { CONFERENCE_COMMANDS } from '@/conference/commands';
import type { ConferenceHandlers } from '@/conference/types';
import type { JitsiLocalTrack, JitsiRemoteTrack } from '@/types/jitsi';
import { TrackProvider, useTrackActions } from '@/contexts/TrackContext';
import { createVirtualBackgroundEffect } from '@/utils/createVirtualBackgroundEffect';
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition';
import apiManager from '@/api';
import apiConfig from '@/api/config';
import { translationActions } from '@/store/slices/translationSlice';
import { getMyCredit, getRoomCredit } from '@/api/translateAPI';
import VideoRenewalView from './VideoRenewalView';
import RoomErrorPage from '@/components/Exceptions/RoomErrorPage';
import { useConferenceInit } from '@/hooks/useConferenceInit';
import { suppressLocalEnvironmentWarningsForExit, useLocalEnvironmentWarnings } from '@/hooks/useLocalEnvironmentWarnings';
import { RenewalDialogProvider } from '@/components/Dialog';
import { store } from '@/store';
import LUXDialog from '@/components/Dialog/LUXDialog';
import { setConferenceManager } from '@/conference/conferenceManagerRef';
import { toastActions } from '@/store/slices/toastSlice';
import { recordingActions } from '@/store/slices/recordingSlice';
import { captionActions } from '@/store/slices/captionSlice';
import { showNotification, dismissNotification } from '@/utils/globalToast';
import { getUserIdentificationCode, getUserIdentify } from '@/utils/userIdentificationCode';
import { toProfileThumbnailUrl } from '@/utils/profileUtils';
import { mapSttHistoryToCaptionEntries } from '@/utils/sttHistory';
import { isHttpNotFoundError, isRoomExplicitlyEnded } from '@/utils/conferenceUtils';
import { getSyncedNow } from '@/utils/timeOffset';

// ─── Inner (TrackContext 안쪽에서 useTrackContext 사용) ──────────────────────

type RoomTokenRequest = Parameters<typeof apiManager.requestRoomToken>[0];

const getQueryParam = (...keys: string[]): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  for (const key of keys) {
    const value = params.get(key);
    if (value) return value;
  }
  return undefined;
};

const getGuestUserId = (localState: LocalState = store.getState().local): string =>
  localState.userId || localState.userIdentify || getUserIdentify();

const CREATOR_CREDIT_TRANSLATION_TYPE: LocalState['aiTranslationType'] = 'USE_CREATOR_CREDIT';
const MASTER_ENDED_MEETING_MESSAGE = '마스터가 이 회의를 종료했습니다.';

const createRoomTokenRequest = (
  roomId: string,
  userName: string,
  localState: LocalState = store.getState().local,
): RoomTokenRequest => {
  if (!localState.isGuest) {
    return { roomId };
  }

  const request: RoomTokenRequest = {
    roomId,
    username: userName || localState.userName || '사용자',
    user: getGuestUserId(localState),
  };

  const emailToken = getQueryParam('token', 'emailToken', 'email_token');
  if (emailToken) request.emailToken = emailToken;

  const joincode = getQueryParam('joincode', 'joinCode') || localState.joinCode;
  if (joincode) request.joincode = joincode;

  return request;
};

const VideoRenewalPageInner: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const localAudioMuted = useAppSelector((s) => s.local.audioMuted);
  const localVideoMuted = useAppSelector((s) => s.local.videoMuted);
  const localHasVideoTrack = useAppSelector((s) => s.local.hasVideoTrack);
  const localJitsiId = useAppSelector((s) => s.local.jitsiId);
  const localUserId = useAppSelector((s) => s.local.userId);
  const localAudioInputDeviceId = useAppSelector((s) => s.local.audioInputDeviceId);
  const isScreenSharing = useAppSelector((s) => s.screenShare.isSharing);
  const virtualBg = useAppSelector((s) => s.virtualBackground);
  const uiScreenMode = useAppSelector((s) => s.ui.screenMode);
  const uiMaxGridSize = useAppSelector((s) => s.ui.maxGridSize);
  const uiVideoQualityOverride = useAppSelector((s) => s.ui.videoQualityOverride);
  const uiRoomStatus = useAppSelector((s) => s.ui.roomStatus);
  const mainUserMainUserId = useAppSelector((s) => s.mainUser.mainUserId);
  const mainUserPinnedUserId = useAppSelector((s) => s.mainUser.pinnedUserId);
  const participantIdsLength = useAppSelector((s) => s.participants.participantIds.length);

  // ── recorder(Jibri)가 방생성자(최초 마스터) 화면을 그대로 구독하기 위한 상태 ──
  const localCreatorPortalId = useAppSelector((s) => s.local.creatorInfo?.portal_id ?? null);
  const mainUserPinnedUserIds = useAppSelector((s) => s.mainUser.pinnedUserIds);
  const uiIsShareClick = useAppSelector((s) => s.ui.isShareClick);
  const uiPresenterOverrideUserId = useAppSelector((s) => s.ui.presenterOverrideUserId);
  const uiGridPage = useAppSelector((s) => s.ui.gridPage);

  const {
    setLocalVideoTrack,
    setLocalAudioTrack,
    setLocalScreenShareTrack,
    setRemoteTrack,
    removeParticipantTracks,
  } = useTrackActions();

  const conferenceManagerRef = useRef<ConferenceManager | null>(null);
  const exitNavigationRef = useRef(false);
  const [isExiting, setIsExiting] = useState(false);
  const loadedSttHistoryRoomsRef = useRef<Set<string>>(new Set());

  // 마스터 켜기 요청 알림 ID 추적 (같은 미디어타입 알림 일괄 dismiss용)
  const unmuteNotifIds = useRef<{ audio: Set<string>; video: Set<string> }>({ audio: new Set(), video: new Set() });

  // 회의 입장 시각 (캐시 메시지 판별용) — onConferenceJoined에서 설정
  const conferenceJoinedAtRef = useRef(0);

  // getMasterUsers race condition 방지용 요청 ID
  const masterRequestIdRef = useRef(0);

  /** getMasterUsers 호출 후 최신 응답만 Redux에 반영. race condition 방지. */
  const fetchAndSyncMasterUsers = useCallback(async (
    token: string,
    commandAttributes?: Record<string, string>,
  ): Promise<MasterUser[] | null> => {
    const requestId = ++masterRequestIdRef.current;
    try {
      const list = await apiManager.getMasterUsers(token);
      if (requestId !== masterRequestIdRef.current) return null; // stale 응답 무시
      dispatch(masterActions.setMasterUsers(list as MasterUser[]));
      conferenceManagerRef.current?.sendCommand(
        CONFERENCE_COMMANDS.SYNC_MASTER_USERS,
        {
          value: JSON.stringify(list),
          ...(commandAttributes ? { attributes: commandAttributes } : {}),
        },
      );
      return list as MasterUser[];
    } catch (err) {
      if (requestId !== masterRequestIdRef.current) return null;
      console.error('[Master] getMasterUsers failed:', err);
      return null;
    }
  }, [dispatch]);

  // ─── STT (Speech-to-Text) — v2 기존 구현 재활용 ────────────────────────
  const caption = useAppSelector((s) => s.caption);
  const handleSendSpeechMessage = useCallback((message: string) => {
    conferenceManagerRef.current?.sendSpeechMessage(message);
  }, []);
  const { isListening } = useSpeechRecognition({
    sendMessage: handleSendSpeechMessage,
    enabled: caption.isEnabled,
    audioMuted: localAudioMuted,
    audioInputDeviceId: localAudioInputDeviceId,
  });
  useLocalEnvironmentWarnings();

  const enableCreatorCreditTranslationOnJoin = useCallback((jitsiId: string) => {
    const language = store.getState().translation.language;

    dispatch(captionActions.setEnabled(true));
    dispatch(uiActions.setCaptionEnabled(true));
    dispatch(voiceNoteActions.setEnabled(true));
    dispatch(translationActions.setCaptionEnabled(true));
    dispatch(translationActions.setTranslateEnabled(true));

    if (!jitsiId) return;

    dispatch(translationActions.setUserTranslationStatus({
      userId: jitsiId,
      enabled: true,
      language,
    }));

    conferenceManagerRef.current?.sendCommand(CONFERENCE_COMMANDS.TRANSLATION_TOGGLE_STATUS, {
      attributes: {
        userId: jitsiId,
        enabled: 'true',
        language,
      },
    });
  }, [dispatch]);

  useEffect(() => {
    const nextMode = caption.isTranscriberJoined
      ? 'jigasi'
      : 'none';
    if (caption.transcriptionMode !== nextMode) {
      dispatch(captionActions.setTranscriptionMode(nextMode));
    }
  }, [caption.isEnabled, caption.isTranscriberJoined, caption.transcriptionMode, dispatch]);

  // init()이 수집한 연결 파라미터를 보관 — handleJoinRoom에서 사용
  const connectParamsRef = useRef<{
    roomId: string;
    userName: string;
    options?: {
      mediaServerToken?: string;
      wehagoId?: string;
      nickname?: string;
      companyCode?: string;
      avatarUrl?: string;
      selectedAvatar?: string;
      userNo?: string;
      nationality?: string;
      startVideoMuted?: boolean;
      startAudioMuted?: boolean;
      audioInputDeviceId?: string;
      videoInputDeviceId?: string;
      // v1 호환 필드
      userIdentificationCode?: string;
      profileUrl?: string;
      companyFullpath?: string;
      isExternalParticipant?: boolean;
      userName2?: string;
      avatar?: string;
      externalUserId?: string;
      userContact?: string;
      userEmail?: string;
      isScreen?: boolean;
      userType?: string;
      host?: boolean;
      isTranslationMode?: string;
      enableAutoLang?: string;
      voiceNoteLocale?: string;
      employeeNo?: string;
      companyNo?: string;
    };
  } | null>(null);

  const navigateToExitPage = useCallback(async (options?: { exitMessage?: string; replace?: boolean; exitTime?: number; roomCreationTime?: number }) => {
    if (exitNavigationRef.current) return;
    exitNavigationRef.current = true;

    suppressLocalEnvironmentWarningsForExit();
    setIsExiting(true);

    const currentState = store.getState();
    const roomId = currentState.local.roomId
      || connectParamsRef.current?.roomId
      || getQueryParam('room', 'roomId')
      || '';

    // dispose() 전에 참석자 목록 캡처 (dispose 후에는 API 조회 시 이미 퇴장한 참여자가 누락됨)
    const exitParticipants: import('@/types/components').ExitParticipant[] = [];
    // 로컬 사용자 추가
    if (currentState.local.userName) {
      exitParticipants.push({
        name: currentState.local.userName,
        imageUrl: currentState.local.profileUrl || undefined,
      });
    }
    // 원격 참여자 추가 (화이트보드/화면공유 가짜 참여자 제외)
    const { participantIds, participants } = currentState.participants;
    for (const pid of participantIds) {
      const p = participants[pid];
      if (p && !p.isWhiteboard && !p.isScreen) {
        exitParticipants.push({
          name: p.displayName || p.nickname || p.userName || '',
          imageUrl: p.profileUrl || p.avatarUrl || undefined,
        });
      }
    }

    try {
      await conferenceManagerRef.current?.dispose();
    } catch (err) {
      console.warn('[VideoRenewalPage] dispose before exit failed:', err);
    } finally {
      conferenceManagerRef.current = null;
      setConferenceManager(null);

      navigate(`/exit?roomId=${encodeURIComponent(roomId)}`, {
        replace: options?.replace,
        state: {
          title: currentState.local.roomName || roomId,
          roomCreationTime: options?.roomCreationTime || currentState.local.startDatetime || currentState.conference.startTime || getSyncedNow(),
          exitTime: options?.exitTime || getSyncedNow(),
          roomId,
          isGuest: currentState.local.isGuest,
          exitMessage: options?.exitMessage,
          exitParticipants,
        },
      });
    }
  }, [dispatch, navigate]);

  // ─── 초기화 ──────────────────────────────────────────────────────────────
  // v1 VideoContainer.init() 흐름:
  //   1. URL ?room= 에서 roomId 추출
  //   2. getUserInfo() → 사용자 정보
  //   3. requestRoomToken(roomId) → Jitsi XMPP mediaServerToken
  //   4. getVideoRoomInfo(roomId) → 방 정보

  const init = useCallback(async () => {
    dispatch(localActions.setConnecting(true));
    dispatch(conferenceActions.setStatus('connecting'));

    const params = new URLSearchParams(window.location.search);
    const roomId = params.get('room') ?? params.get('roomId') ?? 'test-room';

    let userInfo: Record<string, unknown> = {};
    let mediaServerToken: string | undefined;

    // ── 사용자 정보 조회 ─────────────────────────────────────────────────
    try {
      const { resultData } = await apiManager.getUserInfo();
      const profileUrl = String(resultData.profile_url ?? '');
      const absoluteProfileUrl = toProfileThumbnailUrl(profileUrl) ?? '';
      userInfo = { ...resultData, profile_url: absoluteProfileUrl } as Record<string, unknown>;
      dispatch(localActions.setUserInfo(userInfo as Parameters<typeof localActions.setUserInfo>[0]));
    } catch {
      const fallbackId = params.get('userId') || getUserIdentify();
      userInfo = {
        portal_id: fallbackId,
        user_name: params.get('userName') ?? '사용자',
        isGuest: true,
      };
      dispatch(localActions.setUserInfo(userInfo as Parameters<typeof localActions.setUserInfo>[0]));
    }

    // ── 미디어 서버 토큰 조회 ────────────────────────────────────────────
    try {
      const tokenRequest = createRoomTokenRequest(
        roomId,
        String(userInfo.user_name ?? userInfo.userName ?? '사용자'),
      );
      mediaServerToken = await apiManager.requestRoomToken(tokenRequest) as string;
    } catch {
      // 비인증이거나 실패 시 토큰 없이 진행
    }

    // ── 방 정보 조회 ────────────────────────────────────────────────────
    let startDatetime: number | null = null;

    try {
      const roomInfo = await apiManager.getVideoRoomInfo(roomId);
      const info = roomInfo as Record<string, unknown>;

      startDatetime = (info.r_start_datetime as number)
        ?? (info.start_datetime as number)
        ?? (info.create_at as number)
        ?? null;
      const endDatetime = (info.r_end_datetime as number)
        ?? (info.end_datetime as number)
        ?? null;

      if (isRoomExplicitlyEnded(info)) {
        dispatch(localActions.setConnecting(false));
        navigate(`/exit?roomId=${encodeURIComponent(roomId)}`, {
          state: {
            title: (info.name as string) ?? roomId,
            roomCreationTime: (info.r_start_datetime as number) ?? getSyncedNow(),
            exitTime: getSyncedNow(),
            roomId,
            isGuest: store.getState().local.isGuest,
          },
        });
        return;
      }

      // v1: 참여자 수 초과 시 PARTICIPANT_LIMITED
      if (info.r_status === 3) {
        dispatch(uiActions.setRoomStatus('PARTICIPANT_LIMITED'));
        dispatch(localActions.setConnecting(false));
        return;
      }

      // v1: 마스터 거부 시 CONFERENCE_REFUSAL
      if (info.r_status === 4) {
        dispatch(uiActions.setRoomStatus('CONFERENCE_REFUSAL'));
        dispatch(localActions.setConnecting(false));
        return;
      }

      dispatch(
        localActions.setRoomInfo({
          roomId: (info.room as string) ?? roomId,
          roomName: (info.name as string) ?? roomId,
          token: mediaServerToken ?? '',
          isPublic: Boolean(info.is_public),
          securityType: (info.security_type === 'MASTER_APPROVAL' || info.security_type === 'PASSWORD') ? info.security_type as 'MASTER_APPROVAL' | 'PASSWORD' : '',
          userIdentify: getUserIdentify(),
          joinCode: String(info.join_code ?? ''),
          conferenceId: String(info.conference_id ?? ''),
          startDatetime,
          endDatetime,
          creatorInfo: (info.creator_info as import('@/api').CreatorInfo) ?? null,
          linkedMeeting: info.linked_room ? {
            roomId: String(info.linked_room),
            name: String(info.linked_room_name ?? ''),
            startDateTime: (info.linked_room_start_time as number) ?? null,
            endDateTime: (info.linked_room_end_time as number) ?? null,
            accessType: info.is_all_access ? 'ALL' : 'PREVIOUS_ONLY',
          } : null,
          aiTranslationType: (info.ai_translation_type as 'USE_CREATOR_CREDIT' | 'USE_PARTICIPANT_CREDIT' | null) ?? null,
        })
      );

      // ai_translation_type은 AI 번역 모드와 크레딧 정책에만 사용한다.
      // Jigasi는 모든 일반 회의에서 초대하고 실제 STT 소스는 transcriber 입장 상태로 동기화한다.
    } catch (err) {
      if (roomId === 'test-room' || window.location.hostname === 'localhost') {
        dispatch(
          localActions.setRoomInfo({
            roomId,
            roomName: roomId,
            token: mediaServerToken ?? '',
          })
        );
      } else if (isHttpNotFoundError(err)) {
        dispatch(uiActions.setRoomStatus('NOT_FOUND'));
        dispatch(localActions.setConnecting(false));
        return;
      } else {
        dispatch(uiActions.setRoomStatus('NOT_FOUND'));
        dispatch(localActions.setConnecting(false));
        return;
      }
    }

    apiManager.roomExpireTime(roomId).catch(() => {});

    // ── 마스터 목록 선조회 ──
    if (mediaServerToken && !Boolean(userInfo.isGuest)) {
      apiManager.getMasterUsers(mediaServerToken)
        .then((list) => {
          if (Array.isArray(list) && list.length > 0) {
            dispatch(masterActions.setMasterUsers(list as MasterUser[]));
          }
        })
        .catch(() => { /* 대기방에서 재시도한다 */ });
    }

    // ── ONE AI 크레딧 조회 ────────────────────────────────────────────────
    const creditMode = store.getState().local.creditMode;
    const creditPromise = creditMode === 'USER'
      ? getMyCredit()
      : getRoomCredit(roomId);
    creditPromise
      .then((res) => {
        if (res?.resultData != null) {
          dispatch(translationActions.setTotalCredit(res.resultData.total_credit));
        }
      })
      .catch(() => { /* 크레딧 조회 실패 시 무시 — 번역 시 재조회 */ });

    const userName = String(userInfo.user_name ?? userInfo.userName ?? '사용자');
    const isGuestUser = Boolean(userInfo.isGuest);
    const guestUserId = isGuestUser ? String(userInfo.portal_id ?? getUserIdentify()) : '';

    const params2 = new URLSearchParams(window.location.search);
    connectParamsRef.current = {
      roomId,
      userName,
      options: {
        mediaServerToken,
        wehagoId: isGuestUser ? '' : String(userInfo.portal_id ?? ''),
        nickname: String(userInfo.nickname ?? ''),
        companyCode: String(userInfo.company_code ?? ''),
        avatarUrl: String(userInfo.profile_url ?? ''),
        userNo: String(userInfo.user_no ?? ''),
        nationality: String(userInfo.nationality ?? store.getState().translation.language ?? 'ko'),
        // v1 호환 필드
        userIdentificationCode: getUserIdentificationCode(),
        profileUrl: String(userInfo.profile_url ?? ''),
        companyFullpath: String(userInfo.full_path ?? ''),
        isExternalParticipant: isGuestUser,
        userName2: userName,
        avatar: '',         // v1 캐릭터 아바타 — 선택 시 selectedAvatar로 설정
        externalUserId: guestUserId,
        userContact: String(userInfo.user_contact ?? ''),
        userEmail: String(userInfo.user_default_email ?? userInfo.user_email ?? ''),
        isScreen: params2.get('is_screen') === 'true',
        userType: isGuestUser ? '2' : params2.get('type') ?? '',
        host: params2.get('host') === 'true',
        isTranslationMode: 'F',
        enableAutoLang: String(store.getState().translation.isAutoLangEnabled),
        voiceNoteLocale: '',
        employeeNo: String(userInfo.employee_no ?? ''),
        companyNo: String(userInfo.company_no ?? ''),
      },
    };

    if (startDatetime && startDatetime > Date.now()) {
      dispatch(uiActions.setRoomStatus('WAITING'));
    } else {
      dispatch(uiActions.setRoomStatus('SETUP'));
      dispatch(conferenceActions.setStatus('setup'));
    }
    dispatch(localActions.setConnecting(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Jitsi 연결 ──────────────────────────────────────────────────────────

  // 입장·재입장(onConferenceJoined)마다 호출해 자리 비운 사이 대화까지 채운다.
  // loadedSttHistoryRoomsRef는 동시 중복 호출 방지용 in-flight 락.
  const loadSttHistory = useCallback(async (targetRoomId: string) => {
    const currentLocal = store.getState().local;
    if (!targetRoomId || currentLocal.isGuest) return;
    if (loadedSttHistoryRoomsRef.current.has(targetRoomId)) return;

    loadedSttHistoryRoomsRef.current.add(targetRoomId);
    try {
      const history = await apiManager.getSttHistory(targetRoomId);
      const entries = mapSttHistoryToCaptionEntries(history?.chats ?? []);
      if (entries.length > 0) {
        dispatch(captionActions.mergeHistoryEntries(entries));
      }
    } catch (err) {
      console.warn('[STT History] failed to load:', err);
    } finally {
      loadedSttHistoryRoomsRef.current.delete(targetRoomId);
    }
  }, [dispatch]);

  const _connect = useCallback(
    async (
      roomId: string,
      userName: string,
      options?: {
        mediaServerToken?: string;
        wehagoId?: string;
        nickname?: string;
        companyCode?: string;
        avatarUrl?: string;
        selectedAvatar?: string;
        userNo?: string;
        nationality?: string;
        startVideoMuted?: boolean;
        startAudioMuted?: boolean;
        audioInputDeviceId?: string;
        videoInputDeviceId?: string;
        // v1 호환 필드
        userIdentificationCode?: string;
        profileUrl?: string;
        companyFullpath?: string;
        isExternalParticipant?: boolean;
        userName2?: string;
        avatar?: string;
        externalUserId?: string;
        userContact?: string;
        userEmail?: string;
        isScreen?: boolean;
        userType?: string;
        host?: boolean;
        isTranslationMode?: string;
        enableAutoLang?: string;
        voiceNoteLocale?: string;
        employeeNo?: string;
        companyNo?: string;
      }
    ) => {
      if (!window.JitsiMeetJS) {
        dispatch(
          uiActions.showAlert({
            type: 'error',
            message: 'lib-jitsi-meet 라이브러리를 불러올 수 없습니다.',
          })
        );
        return;
      }

      dispatch(localActions.setConnecting(true));
      dispatch(conferenceActions.setStatus('connecting'));

      const handlers: ConferenceHandlers = {
        onConferenceJoined: async () => {
          conferenceJoinedAtRef.current = Date.now();
          dispatch(localActions.setConnected(true));
          dispatch(conferenceActions.setStatus('joined'));
          void loadSttHistory(roomId);

          const myJitsiId = conferenceManagerRef.current?.getRoom()?.myUserId() ?? '';
          if (store.getState().local.aiTranslationType === CREATOR_CREDIT_TRANSLATION_TYPE) {
            enableCreatorCreditTranslationOnJoin(myJitsiId);
          }
          // mediaServerToken: options → Redux local.token → 재조회 순서로 시도
          let token = options?.mediaServerToken || store.getState().local.token;
          if (!token) {
            try {
              token = await apiManager.requestRoomToken(
                createRoomTokenRequest(roomId, userName),
              ) as string;
              if (token) {
                dispatch(localActions.mergeRoomInfo({ token }));
              }
            } catch (e) {
              console.error('[room] requestRoomToken failed', e);
            }
          }
          console.log('[Master] token resolved:', token ? token.substring(0, 20) + '...' : 'NONE');
          // recorder(Jibri)는 실제 참가자가 아니므로 접속자 등록(/connect)을 하지 않는다.
          if (!isRecorderMode()) {
            if (!token) {
              console.error('[connect] skip insertUser: no room token');
            } else {
              apiManager.insertUser({
                videoseq: myJitsiId,
                token,
                username: userName,
                uid: undefined,
              }).catch((e) => console.error('[connect] insertUser failed', e));
            }
          }

          // ONE AI 배포 여부 확인
          apiManager.getIsServiceDeploy('one-ai')
            .then((result) => {
              const isAble = result?.isServiceDeploy === 'T';
              dispatch(translationActions.setIsAbleOneAI(isAble));
            })
            .catch(() => {
              dispatch(translationActions.setIsAbleOneAI(false));
            });

          if (token) {
            fetchAndSyncMasterUsers(token);
          } else {
            console.warn('[Master] no mediaServerToken on CONFERENCE_JOINED (even after retry)');
          }
        },
        onConferenceFailed: (errorCode) => {
          if (errorCode === 'kicked') {
            // zombie room 방지: 다이얼로그 표시 전에 즉시 dispose하여
            // useEffect 재실행 시 zombie room 참조를 막는다
            conferenceManagerRef.current?.dispose();

            const isGuest = store.getState().local.isGuest;
            const leaveAction = isGuest
              ? () => { window.history.back(); }
              : () => { window.location.href = apiConfig.portalBaseUrl + '/wehagomeet/#/'; };
            LUXDialog.alert('마스터에 의해 회의에서 내보내졌습니다.', {
              type: 'error',
              title: '회의 퇴장',
              confirmCallback: leaveAction,
              onCloseCallback: leaveAction,
            });
            return;
          }
          if (errorCode === 'duplicate_login') {
            // Jitsi 이벤트가 Redux 상태를 idle로 바꿔 화면이 전환되는 것을 차단
            exitNavigationRef.current = true;
            
            // 연결은 즉시 끊어 카메라/마이크를 끄고 타인에게서 내 모습을 지움
            conferenceManagerRef.current?.dispose();

            const leaveAction = () => {
              // 확인 버튼 클릭 시 막아뒀던 상태를 풀고 수동으로 대기방(idle)으로 이동
              exitNavigationRef.current = false;
              dispatch(conferenceActions.setStatus('idle'));
              dispatch(localActions.setConnected(false));
              dispatch(participantsActions.reset());
              dispatch(masterActions.reset());
              setLocalVideoTrack(null);
              setLocalAudioTrack(null);
            };
            
            LUXDialog.alert('동시 접속으로 인해 연결이 종료되었습니다.', {
              type: 'error',
              title: '회의 퇴장',
              confirmCallback: leaveAction,
              onCloseCallback: leaveAction,
            });
            return;
          }
          dispatch(conferenceActions.setError({ code: errorCode, message: errorCode }));
          dispatch(uiActions.showAlert({ type: 'error', message: `회의 연결 실패: ${errorCode}` }));
          apiManager.sendLogSystem({
            message: 'CONFERENCE_FAILED',
            error: JSON.stringify({ errorCode }),
            errorType: 'conference_failed',
          }).catch(() => {});
        },
        onEndMeetingForAll: (exitTime: number, roomCreationTime?: number) => {
          if (exitNavigationRef.current) return;
          const leaveAction = () => { void navigateToExitPage({ exitMessage: MASTER_ENDED_MEETING_MESSAGE, replace: true, exitTime, roomCreationTime }); };

          LUXDialog.alert(MASTER_ENDED_MEETING_MESSAGE, {
            type: 'error',
            title: '회의 퇴장',
            confirmCallback: leaveAction,
            onCloseCallback: leaveAction,
          });
          leaveAction();
        },
        onConferenceLeft: () => {
          if (exitNavigationRef.current) return;
          dispatch(conferenceActions.setStatus('idle'));
          dispatch(localActions.setConnected(false));
          dispatch(participantsActions.reset());
          dispatch(masterActions.reset());
          setLocalVideoTrack(null);
          setLocalAudioTrack(null);
        },
        onConnectionFailed: (errorCode) => {
          dispatch(uiActions.setConnectionDropped(true));
          dispatch(localActions.setConnecting(false));
          apiManager.sendLogSystem({
            message: 'CONNECTION_FAILED',
            error: JSON.stringify({ errorCode }),
            errorType: 'connection_failed',
          }).catch(() => {});
        },
        onConnectionDisconnected: () => {
          dispatch(localActions.setReconnecting(true));
          // dispatch(uiActions.showAlert({ type: 'warning', message: '연결이 끊겼습니다. 재연결 중...' }));
        },

        // 로컬 트랙 — TrackContext 업데이트
        onLocalAudioTrackAdded: (track: JitsiLocalTrack) => {
          setLocalAudioTrack(track);
          dispatch(localActions.setHasAudioTrack(true));
          dispatch(localActions.setAudioMuted(track.isMuted()));
        },
        onLocalVideoTrackAdded: (track: JitsiLocalTrack) => {
          setLocalVideoTrack(track);
          dispatch(localActions.setHasVideoTrack(true));
          dispatch(localActions.setVideoMuted(track.isMuted()));
        },
        onLocalAudioTrackRemoved: (_track: JitsiLocalTrack) => {
          setLocalAudioTrack(null);
          dispatch(localActions.setHasAudioTrack(false));
        },
        onLocalVideoTrackRemoved: (_track: JitsiLocalTrack) => {
          setLocalVideoTrack(null);
          dispatch(localActions.setHasVideoTrack(false));
        },
        onLocalAudioMuteChanged: (muted: boolean) => {
          dispatch(localActions.setAudioMuted(muted));
        },
        onLocalVideoMuteChanged: (muted: boolean) => {
          dispatch(localActions.setVideoMuted(muted));
        },

        // 화면 공유 트랙 (카메라와 별도)
        onLocalScreenShareTrackAdded: (track: JitsiLocalTrack) => {
          setLocalScreenShareTrack(track);
        },
        onLocalScreenShareTrackRemoved: (_track: JitsiLocalTrack) => {
          setLocalScreenShareTrack(null);
        },

        dispatch,

        onRecordingStateChanged: (isRecording, _sessionId) => {
          if (isRecording) {
            apiManager.findRecordingInfo(roomId)
              .then((info: any) => {
                if (info) {
                  dispatch(recordingActions.setRequestUser(info.user ?? null));
                  dispatch(recordingActions.setStartDateTime(info.start_date_time ?? null));
                }
              })
              .catch(() => {});
            // 폴링 중이면 중단 (이벤트로 정상 수신됨)
            if (recordingPollingRef.current) {
              clearInterval(recordingPollingRef.current);
              recordingPollingRef.current = null;
            }
            dispatch(toastActions.showSnackbar({
              type: 'success',
              message: '녹화가 시작되었습니다.',
            }));
          } else {
            dispatch(toastActions.showSnackbar({
              type: 'info',
              message: '녹화가 종료되었습니다.',
            }));
          }
        },

        // 녹화 에러 핸들러 (공식 jitsi-meet _showRecordingErrorNotification 패턴)
        onRecordingError: (error: string) => {
          let message: string;
          switch (error) {
            case 'service-unavailable':
              message = '녹화 서비스를 사용할 수 없습니다. 잠시 후 다시 시도해주세요.';
              break;
            case 'resource-constraint':
              message = '녹화 서버가 사용 중입니다. 잠시 후 다시 시도해주세요.';
              break;
            case 'unexpected-request':
              message = '이미 녹화가 진행 중입니다.';
              break;
            default:
              message = '녹화 중 오류가 발생했습니다.';
              break;
          }
          dispatch(toastActions.showSnackbar({ type: 'error', message }));
          dispatch(recordingActions.setStatus('idle'));
        },

        onMasterListChanged: async () => {
          let t = options?.mediaServerToken || store.getState().local.token;
          if (!t) {
            try { t = await apiManager.requestRoomToken(createRoomTokenRequest(roomId, userName)) as string; } catch { /* */ }
          }
          if (t) {
            fetchAndSyncMasterUsers(t);
          }
        },
        onRemainingMasterRequested: () => {
          apiManager.getRemainingMaster(roomId).catch(() => {});
        },
        onRoomPublicModeInit: () => {
          apiManager.getRoomIsPublic(roomId)
            .then((isPublic) => {
              dispatch(localActions.mergeRoomInfo({ isPublic: Boolean(isPublic) }));
            })
            .catch(() => {});
        },
        onMasterMicControlChanged: (controlType) => {
          const t = options?.mediaServerToken || store.getState().local.token;
          if (t) {
            apiManager.updateMasterControlUser(t, {
              audioActive: controlType === 'unmute',
            }).catch(() => {});
          }
        },
        onMasterVideoSeqChanged: (videoseq) => {
          const t = options?.mediaServerToken || store.getState().local.token;
          if (t) {
            apiManager.updateMasterControlUser(t, {
              videoseq,
            }).catch(() => {});
          }
        },

        onRecordRequested: (userId) => {
          apiManager.recordRequest(roomId, userId).catch(() => {});
        },
        onUpdateRecordData: (data) => {
          apiManager.updateRecordData(roomId, data).catch(() => {});
        },

        onMasterPinFixed: (masterPinnedUserIds, userName, type, isScreenShare, actionTimestamp) => {
          // 서버가 동기화를 위해 이전 캐시를 다시 쏘는(에코) 경우 무시 (토스트 중복 방지)
          // 1. Timestamp 비교 (3초 이상 지난 캐시 메시지인지 확인)
          // actionTimestamp가 없는 경우(모바일 등):
          //   - conferenceJoinedAtRef === 0 이면 아직 onConferenceJoined 전 (초기 presence 수신) → 캐시
          //   - 입장 후 5초 이내 → 캐시 (늦은 참가자 presence echo)
          //   - 그 외 → 실제 새 액션
          const isCacheMessage = actionTimestamp
            ? (getSyncedNow() - actionTimestamp) > 3000
            : conferenceJoinedAtRef.current === 0 || (Date.now() - conferenceJoinedAtRef.current) < 5000;
          if (isCacheMessage) {
            // 늦은 입장자의 경우 상태만 맞추고 토스트는 띄우지 않음
            // 이미 동일한 pin 상태면 screenMode 변경 없이 무시 (presence 재전송에 의한 강제전환 방지)
            const currentPinned = store.getState().mainUser.masterPinnedUserIds;
            if (JSON.stringify(currentPinned) === JSON.stringify(masterPinnedUserIds)) {
              return;
            }
            dispatch(mainUserActions.setMasterPinnedUsers(masterPinnedUserIds));
            // 고정된 상태라면 PRESENTER 모드로 전환하여 기존 참여자와 동일한 화면 노출
            if (type === 'pin' && masterPinnedUserIds.length > 0) {
              dispatch(uiActions.setScreenMode('PRESENTER'));
              dispatch(uiActions.setPresenterOverrideUserId(null));
            }
            return;
          }

          // 2. Fallback: 상태가 완전히 동일하면 무시
          const currentPinned = store.getState().mainUser.masterPinnedUserIds;
          if (JSON.stringify(currentPinned) === JSON.stringify(masterPinnedUserIds)) {
            return;
          }

          dispatch(mainUserActions.setMasterPinnedUsers(masterPinnedUserIds));
          // 공유 화면의 경우 "마스터가 OOO님의 공유 화면을 고정했습니다"
          // 일반 화면의 경우 "마스터가 OOO님 화면을 고정했습니다"
          const message = isScreenShare
            ? `마스터가 ${userName}님의 공유 화면을 ${type === 'pin' ? '고정했습니다.' : '고정 해제했습니다.'}`
            : `마스터가 ${userName}님 화면을 ${type === 'pin' ? '고정했습니다.' : '고정 해제했습니다.'}`;
          dispatch(toastActions.showSnackbar({
            type: 'info',
            message,
          }));
          if (type === 'pin') {
            dispatch(uiActions.setScreenMode('PRESENTER'));
            dispatch(uiActions.setPresenterOverrideUserId(null));
          }
        },

        onMasterMediaControl: (action, mediaType) => {
          const m = conferenceManagerRef.current;
          if (!m) return;
          if (action === 'mute') {
            if (mediaType === 'audio') {
              const currentLocal = store.getState().local;
              if (!currentLocal.audioMuted) m.audioMute();
              dispatch(toastActions.showSnackbar({ type: 'info', message: '마스터에 의해 마이크가 꺼졌습니다.' }));
            } else {
              const currentLocal = store.getState().local;
              if (!currentLocal.videoMuted) m.videoMute();
              dispatch(toastActions.showSnackbar({ type: 'info', message: '마스터에 의해 카메라가 꺼졌습니다.' }));
            }
          } else if (action === 'unmute') {
            if (mediaType === 'audio') {
              const currentLocal = store.getState().local;
              if (currentLocal.audioMuted) m.audioUnmute();
              dispatch(toastActions.showSnackbar({ type: 'info', message: '마이크가 켜졌습니다.' }));
            } else {
              const currentLocal = store.getState().local;
              if (currentLocal.videoMuted) m.videoUnmute();
              dispatch(toastActions.showSnackbar({ type: 'info', message: '카메라가 켜졌습니다.' }));
            }
          } else if (action === 'request_unmute') {
            const currentLocal = store.getState().local;
            if (mediaType === 'video') {
              if (!currentLocal.videoMuted) return; // 이미 켜져있으면 무시
              const nid = showNotification({
                text: '마스터가 카메라 사용을 요청하였습니다.',
                buttons: [
                  { label: '다음에', onClick: () => {
                    unmuteNotifIds.current.video.delete(nid);
                  } },
                  { label: '카메라 켜기', variant: 'blue', onClick: () => {
                    conferenceManagerRef.current?.videoUnmute();
                    // 같은 미디어타입 알림 일괄 dismiss
                    for (const id of unmuteNotifIds.current.video) {
                      if (id !== nid) dismissNotification(id);
                    }
                    unmuteNotifIds.current.video.clear();
                  }},
                ],
              });
              unmuteNotifIds.current.video.add(nid);
            } else {
              if (!currentLocal.audioMuted) return; // 이미 켜져있으면 무시
              const nid = showNotification({
                text: '마스터가 마이크 사용을 요청하였습니다.',
                buttons: [
                  { label: '다음에', onClick: () => {
                    unmuteNotifIds.current.audio.delete(nid);
                  } },
                  { label: '마이크 켜기', variant: 'blue', onClick: () => {
                    conferenceManagerRef.current?.audioUnmute();
                    for (const id of unmuteNotifIds.current.audio) {
                      if (id !== nid) dismissNotification(id);
                    }
                    unmuteNotifIds.current.audio.clear();
                  }},
                ],
              });
              unmuteNotifIds.current.audio.add(nid);
            }
          }
        },

        onMasterPermissionChanged: (action, userName) => {
          const localState = store.getState().local;
          const targetName = userName || localState.userName || localState.nickname || '참석자';
          dispatch(toastActions.showSnackbar({
            type: 'success',
            message: action === 'grant'
              ? `${targetName}님께 마스터 권한이 부여되었습니다.`
              : `${targetName}님의 마스터 권한이 해제되었습니다.`,
          }));
        },

        onEmojiReactionReceived: (emojiUserList) => {
          // 다른 참여자로부터 받은 이모지 리액션 처리
          dispatch(emojiActions.clearEmojis());
          emojiUserList.forEach((emoji) => {
            dispatch(emojiActions.addEmoji({
              id: `${emoji.userId}-${emoji.timestamp}`,
              userId: emoji.userId,
              userName: emoji.userName || '',
              emoji: emoji.emoji,
              timestamp: emoji.timestamp,
              type: emoji.type as 'emoji' | 'character',
            }));
          });
        },
      };

      const manager = new ConferenceManager();

      manager.onRemoteTrackAdded = (participantId: string, track: JitsiRemoteTrack) => {
        const mediaType = track.getType();
        const videoType = (track as any).videoType as string | undefined;
        if (mediaType === 'audio') {
          setRemoteTrack(participantId, 'audio', track);
        } else if (videoType === 'desktop') {
          setRemoteTrack(participantId, 'screenShare', track);
        } else {
          setRemoteTrack(participantId, 'video', track);
        }
      };
      manager.onRemoteTrackRemoved = (participantId: string, track: JitsiRemoteTrack) => {
        const mediaType = track.getType();
        const videoType = (track as any).videoType as string | undefined;
        const videoTypeAlt = typeof (track as any).getVideoType === 'function'
          ? (track as any).getVideoType() as string | undefined
          : undefined;
        const resolvedVideoType = videoType || videoTypeAlt;
        if (mediaType === 'audio') {
          setRemoteTrack(participantId, 'audio', null);
        } else if (resolvedVideoType === 'desktop') {
          setRemoteTrack(participantId, 'screenShare', null);
        } else {
          setRemoteTrack(participantId, 'video', null);
        }
      };
      manager.onParticipantLeft = (participantId: string) => {
        removeParticipantTracks(participantId);
      };

      conferenceManagerRef.current = manager;
      setConferenceManager(manager);

      try {
        await manager.connect(roomId, userName, handlers, options);
      } catch (err) {
        console.error('[VideoRenewalPage] connect error:', err);
        dispatch(uiActions.showAlert({ type: 'error', message: '회의 연결 중 오류가 발생했습니다.' }));
        dispatch(localActions.setConnecting(false));
      }
    },
    [dispatch, enableCreatorCreditTranslationOnJoin, loadSttHistory, navigateToExitPage, setLocalAudioTrack, setLocalVideoTrack, setLocalScreenShareTrack, setRemoteTrack, removeParticipantTracks]
  );

  // ─── 수신 비디오 해상도 동적 조절 ────────────────────────────────────────
  // /video-renewal에서는 screenMode 직접 전달 (GRID / SIDE / PRESENTER)
  // visibleCount: 본인 포함 한 페이지 표시 수 (GRID) 또는 원격 참여자 수 (SIDE/PRESENTER)

  useEffect(() => {
    const m = conferenceManagerRef.current;
    console.log('%c[VideoQuality useEffect]', 'color:#0f0;font-weight:bold',
      'screenMode:', uiScreenMode, 'manager:', !!m,
      'override:', uiVideoQualityOverride,
      'mainUserId:', mainUserPinnedUserId ?? mainUserMainUserId,
      'participantCount:', participantIdsLength,
    );
    if (!m) return;

    const remoteCount = participantIdsLength;
    // GRID: 본인 포함 화면 표시 수 = min(remoteCount + 1, maxGridSize)
    // recorder 모드는 자기 자신 타일을 그리지 않으므로(+1 슬롯 불필요) 원격 인원수만 사용
    const visibleCount = uiScreenMode === 'GRID'
      ? (isRecorderMode() ? Math.min(remoteCount, uiMaxGridSize) : Math.min(remoteCount + 1, uiMaxGridSize))
      : remoteCount;

    // 테스트 오버라이드 반영
    const ov = uiVideoQualityOverride;
    let overrides: { main?: number | null; sub?: number | null; gridTiers?: import('@/conference/config/videoQualityPolicy').GridTier[] | null; gridFallback?: import('@/conference/config/videoQualityPolicy').VideoQualityLevel | null } | undefined;
    if (ov) {
      if (uiScreenMode === 'GRID') {
        overrides = (ov.grid != null || ov.gridTiers != null || ov.gridFallback != null)
          ? { main: ov.grid, gridTiers: ov.gridTiers, gridFallback: ov.gridFallback }
          : undefined;
      } else if (uiScreenMode === 'PRESENTER') {
        overrides = (ov.presenterMain != null || ov.presenterSub != null)
          ? { main: ov.presenterMain, sub: ov.presenterSub }
          : undefined;
      } else {
        overrides = (ov.sideMain != null || ov.sideSub != null)
          ? { main: ov.sideMain, sub: ov.sideSub }
          : undefined;
      }
    }

    console.log('%c[VideoQuality useEffect]', 'color:#0f0;font-weight:bold', 'calling updateReceiverQuality →', {
      screenMode: uiScreenMode, visibleCount, mainUserId: mainUserPinnedUserId ?? mainUserMainUserId, overrides,
    });

    m.updateReceiverQuality(
      uiScreenMode,
      mainUserPinnedUserId ?? mainUserMainUserId,
      visibleCount,
      overrides,
    );
  }, [uiScreenMode, uiMaxGridSize, uiVideoQualityOverride, mainUserMainUserId, mainUserPinnedUserId, participantIdsLength]);

  // ─── 가상 배경 효과 적용 ──────────────────────────────────────────────────

  useEffect(() => {
    const m = conferenceManagerRef.current;
    if (!m || !localHasVideoTrack) return;

    if (!virtualBg.isEnabled || virtualBg.type === 'none') {
      m.setVirtualBackgroundEffect(undefined).catch(() => {});
      return;
    }

    const bgType = (virtualBg.type === 'blur' || virtualBg.type === 'slight_blur') ? 'blur' : 'image';
    const blurVal = virtualBg.type === 'slight_blur' ? 30 : virtualBg.blurValue;
    createVirtualBackgroundEffect({
      backgroundType: bgType,
      virtualSource: bgType === 'image' ? (virtualBg.imageUrl ?? undefined) : undefined,
      blurValue: blurVal,
    }).then((effect) => {
      if (effect) m.setVirtualBackgroundEffect(effect).catch(() => {});
    }).catch(() => {});
  }, [virtualBg.isEnabled, virtualBg.type, virtualBg.blurValue, virtualBg.imageUrl, localHasVideoTrack]);

  // ─── 생명주기 ─────────────────────────────────────────────────────────────

  useEffect(() => {
    init();

    const handleBeforeUnload = () => {
      suppressLocalEnvironmentWarningsForExit();
      conferenceManagerRef.current?.dispose();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      conferenceManagerRef.current?.dispose();
      conferenceManagerRef.current = null;
      setConferenceManager(null);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── 참여하기 (PreMeetingPage → 실제 Jitsi 연결) ─────────────────────────

  const handleJoinRoom = useCallback(async () => {
    const params = connectParamsRef.current;
    if (!params) return;

    const currentLocal = store.getState().local;
    const finalUserName = currentLocal.userName || params.userName || '사용자';
    const guestUserId = currentLocal.isGuest ? getGuestUserId(currentLocal) : '';
    let mediaServerToken = params.options?.mediaServerToken || currentLocal.token;

    if (!mediaServerToken) {
      try {
        mediaServerToken = await apiManager.requestRoomToken(
          createRoomTokenRequest(params.roomId, finalUserName, currentLocal),
        ) as string;
        if (mediaServerToken) {
          dispatch(localActions.mergeRoomInfo({ token: mediaServerToken }));
        }
      } catch {
        // 연결 실패 처리는 _connect/Jitsi 이벤트에서 이어서 처리한다.
      }
    }

    await _connect(params.roomId, finalUserName, {
      ...params.options,
      mediaServerToken,
      wehagoId: currentLocal.isGuest ? '' : params.options?.wehagoId,
      nickname: currentLocal.nickname || params.options?.nickname,
      selectedAvatar: currentLocal.selectedAvatar,
      isExternalParticipant: currentLocal.isGuest || params.options?.isExternalParticipant,
      userName2: finalUserName,
      externalUserId: currentLocal.isGuest ? guestUserId : params.options?.externalUserId,
      userType: currentLocal.isGuest ? '2' : params.options?.userType,
      startVideoMuted: currentLocal.videoMuted,
      startAudioMuted: currentLocal.audioMuted,
      nationality: store.getState().translation.language,
      enableAutoLang: String(store.getState().translation.isAutoLangEnabled),
      audioInputDeviceId: currentLocal.audioInputDeviceId,
      videoInputDeviceId: currentLocal.videoInputDeviceId,
    });
  }, [_connect]);

  // ─── Recorder(Jibri) 모드: 프리조인 건너뛰고 자동 입장 ────────────────────
  const recorderAutoJoinedRef = useRef(false);
  useEffect(() => {
    if (!isRecorderMode()) return;
    if (uiRoomStatus !== 'SETUP') return;
    if (recorderAutoJoinedRef.current) return;
    recorderAutoJoinedRef.current = true;

    // recorder는 카메라/마이크 없이 입장한다.
    dispatch(localActions.setAudioMuted(true));
    dispatch(localActions.setVideoMuted(true));

    let cancelled = false;
    void (async () => {
      const start = Date.now();
      while ((!window.JitsiMeetJS || !connectParamsRef.current) && Date.now() - start < 20000) {
        await new Promise((r) => { setTimeout(r, 200); });
      }
      if (cancelled) return;
      try {
        await handleJoinRoom();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[recorder] auto-join failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, [uiRoomStatus, handleJoinRoom]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── 방생성자(최초 마스터) 화면 상태 브로드캐스트 ───────────────────────────
  const isRoomCreator = Boolean(localUserId) && localUserId === localCreatorPortalId;
  useEffect(() => {
    if (!isRoomCreator) return;
    const m = conferenceManagerRef.current;
    if (!m) return;

    m.sendCommand(CONFERENCE_COMMANDS.SYNC_CREATOR_SCREEN, {
      value: JSON.stringify({
        screenMode: uiScreenMode,
        isShareClick: uiIsShareClick,
        presenterOverrideUserId: uiPresenterOverrideUserId,
        gridPage: uiGridPage,
        mainUserId: mainUserMainUserId,
        pinnedUserId: mainUserPinnedUserId,
        pinnedUserIds: mainUserPinnedUserIds,
      }),
    });
  }, [
    isRoomCreator,
    uiScreenMode,
    uiIsShareClick,
    uiPresenterOverrideUserId,
    uiGridPage,
    mainUserMainUserId,
    mainUserPinnedUserId,
    mainUserPinnedUserIds,
  ]);

  // ─── 액션 핸들러 ─────────────────────────────────────────────────────────

  const handleToggleAudio = useCallback(async () => {
    const m = conferenceManagerRef.current;
    if (!m) return;
    if (localAudioMuted) {
      await m.audioUnmute();
    } else {
      await m.audioMute();
    }
  }, [localAudioMuted]);

  const handleToggleVideo = useCallback(async () => {
    const m = conferenceManagerRef.current;
    if (!m) return;
    if (localVideoMuted) {
      await m.videoUnmute();
    } else {
      await m.videoMute();
    }
  }, [localVideoMuted]);

  const handleToggleScreenShare = useCallback(async () => {
    const m = conferenceManagerRef.current;
    if (!m) return;
    try {
      if (isScreenSharing) {
        await m.stopScreenShare();
      } else {
        await m.startScreenShare();
      }
    } catch (err) {
      console.error('[VideoRenewalPage] screen share error:', err);
    }
  }, [isScreenSharing]);

  const handleHangup = useCallback(() => {
    void navigateToExitPage();
  }, [navigateToExitPage]);

  const handleEndMeetingForAll = useCallback(() => {
    const exitTime = getSyncedNow();
    const currentState = store.getState();
    const roomCreationTime = currentState.local.startDatetime || currentState.conference.startTime || getSyncedNow();
    conferenceManagerRef.current?.sendCommand(
      CONFERENCE_COMMANDS.END_MEETING_FOR_ALL,
      { value: String(exitTime), attributes: { roomCreationTime: String(roomCreationTime) } },
    );
    void navigateToExitPage({ exitMessage: MASTER_ENDED_MEETING_MESSAGE, replace: true, exitTime, roomCreationTime });
  }, [navigateToExitPage]);

  const handleSendMessage = useCallback((message: string) => {
    conferenceManagerRef.current?.sendChatMessage(message);
  }, []);

  // 강퇴 로그 적재는 ConferenceManager.kickParticipant에서 처리한다
  const handleKickParticipant = useCallback((userId: string) => {
    conferenceManagerRef.current?.kickParticipant(userId);
  }, []);

  const handleGrantMaster = useCallback(async (userId: string): Promise<boolean> => {
    const state = store.getState();
    const token = state.local.token;
    if (!token) {
      dispatch(toastActions.showSnackbar({ type: 'error', message: '마스터 권한 변경에 필요한 회의 토큰이 없습니다.' }));
      return false;
    }
    const p = state.participants.participants[userId];
    const wehagoId = p?.wehagoId;
    if (!wehagoId) {
      dispatch(toastActions.showSnackbar({ type: 'error', message: '참여자 회원 정보가 아직 동기화되지 않았습니다.' }));
      return false;
    }
    try {
      await apiManager.grantMaster(token, [wehagoId]);
      const list = await fetchAndSyncMasterUsers(token, {
        action: 'grant',
        senderId: conferenceManagerRef.current?.getRoom?.()?.myUserId?.() ?? state.local.jitsiId,
        target: userId,
        userName: p.displayName || p.nickname || '',
      });
      return list !== null;
    } catch (err) {
      console.warn('[Master] grantMaster 실패:', err);
      dispatch(toastActions.showSnackbar({ type: 'error', message: '마스터 권한 부여에 실패했습니다.' }));
      return false;
    }
  }, [dispatch, fetchAndSyncMasterUsers]);

  const handleClearMaster = useCallback(async (userId: string): Promise<boolean> => {
    const state = store.getState();
    const token = state.local.token;
    if (!token) {
      dispatch(toastActions.showSnackbar({ type: 'error', message: '마스터 권한 변경에 필요한 회의 토큰이 없습니다.' }));
      return false;
    }
    const p = state.participants.participants[userId];
    const wehagoId = p?.wehagoId;
    if (!wehagoId) {
      dispatch(toastActions.showSnackbar({ type: 'error', message: '참여자 회원 정보가 아직 동기화되지 않았습니다.' }));
      return false;
    }
    try {
      await apiManager.clearMaster(token, [wehagoId]);
      const list = await fetchAndSyncMasterUsers(token, {
        action: 'clear',
        senderId: conferenceManagerRef.current?.getRoom?.()?.myUserId?.() ?? state.local.jitsiId,
        target: userId,
        userName: p.displayName || p.nickname || '',
      });
      return list !== null;
    } catch (err) {
      console.warn('[Master] clearMaster 실패:', err);
      dispatch(toastActions.showSnackbar({ type: 'error', message: '마스터 권한 해제에 실패했습니다.' }));
      return false;
    }
  }, [dispatch, fetchAndSyncMasterUsers]);

  const handleSelectParticipant = useCallback((userId: string | null) => {
    conferenceManagerRef.current?.selectParticipant(userId);
  }, []);

  // ── 녹화 폴링 (V1 checkRecordStart 패턴) ──────────────────────────────────
  const recordingPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startRecordingPolling = useCallback((room: string) => {
    if (recordingPollingRef.current) {
      clearInterval(recordingPollingRef.current);
    }
    let attempts = 0;
    const MAX_ATTEMPTS = 30; // 최대 30초

    recordingPollingRef.current = setInterval(async () => {
      attempts++;
      try {
        const recordingInfo = await apiManager.findRecordingInfo(room) as {
          record_session_id?: string;
          user?: string;
          start_date_time?: string;
        } | null;

        if (recordingInfo?.start_date_time) {
          dispatch(recordingActions.setRequestUser(recordingInfo.user ?? null));
          dispatch(recordingActions.setStartDateTime(recordingInfo.start_date_time));
          if (recordingInfo.record_session_id) {
            dispatch(recordingActions.setRecording({
              isRecording: true,
              sessionId: recordingInfo.record_session_id,
            }));
          }
          clearInterval(recordingPollingRef.current!);
          recordingPollingRef.current = null;
        }
      } catch {
        // API 에러는 무시, 계속 폴링
      }

      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(recordingPollingRef.current!);
        recordingPollingRef.current = null;
        
        // V1과 동일하게 API가 null을 반환(또는 타임아웃)하더라도 
        // 조용히 폴링만 중단하며, 실패 상태로 강제 전환하지 않음.
        // 실제 녹화 실패 여부는 XMPP RECORDER_STATE_CHANGED 이벤트의 에러 필드로만 판단함.
      }
    }, 1000);
  }, [dispatch]);

  // 폴링 cleanup
  useEffect(() => {
    return () => {
      if (recordingPollingRef.current) {
        clearInterval(recordingPollingRef.current);
      }
    };
  }, []);

  const handleToggleRecording = useCallback(async () => {
    const m = conferenceManagerRef.current;
    if (!m) return;
    const { isRecording, sessionId } = store.getState().recording;

    // ── 녹화 중지 ──
    if (isRecording && sessionId) {
      try {
        await m.stopRecording(sessionId);
      } catch (err) {
        console.error('[Recording] stopRecording 실패:', err);
        dispatch(toastActions.showSnackbar({
          type: 'error',
          message: '녹화 중지에 실패했습니다.',
        }));
      }
      return;
    }

    // ── 녹화 시작: 1) 확인 다이얼로그 (V1 RecordSelectDialog 패턴) ──
    const confirmed = await new Promise<boolean>((resolve) => {
      LUXDialog.confirm(
        '회의를 기록하고 저장할 수 있습니다.\n녹화된 영상은 회의 기록에서 조회 가능합니다.',
        {
          title: '회의녹화',
          type: 'info',
          useRenewalStyle: true,
          confirmCallback: () => resolve(true),
          cancelCallback: () => resolve(false),
        }
      );
    });
    if (!confirmed) return;

    // ── 녹화 시작: 2) 재녹화 경고 (V1 confirmReRecording 패턴) ──
    try {
      const token = store.getState().local.token;
      if (token) {
        const recordedCount = await apiManager.recordedCount(token);
        if (recordedCount > 0) {
          const reConfirmed = await new Promise<boolean>((resolve) => {
            LUXDialog.confirm(
              '기존 녹화 파일이 삭제되고\n새로 녹화가 시작됩니다.',
              {
                title: '재녹화',
                type: 'warning',
                useRenewalStyle: true,
                confirmCallback: () => resolve(true),
                cancelCallback: () => resolve(false),
              }
            );
          });
          if (!reConfirmed) return;
        }
      }
    } catch (err) {
      console.warn('[Recording] recordedCount 조회 실패, 계속 진행:', err);
    }

    // ── 녹화 시작: 3) 실행 + 에러 처리 ──
    try {
      const roomId = store.getState().local.roomId;
      const jitsiId = m.getRoom()?.myUserId() || store.getState().local.jitsiId || 'unknown';

      // 녹화 요청 등록 API 호출 (레거시 V1 REQUEST_RECORD_USER 패턴 복구)
      // 백엔드는 request_user로 Jitsi Resource ID를 기대하므로 jitsiId를 전달
      apiManager.recordRequest(roomId, jitsiId).catch((err) => {
        console.warn('[Recording] recordRequest 실패:', err);
      });

      dispatch(recordingActions.setStatus('pending'));
      dispatch(toastActions.showSnackbar({
        type: 'info',
        message: '녹화를 시작합니다.',
      }));

      await m.startRecording({
        mode: 'file',
        appData: JSON.stringify({ file_recording_metadata: { share: true } }),
      });

      // ── 녹화 시작: 4) 폴링 보상 (V1 checkRecordStart 패턴) ──
      startRecordingPolling(roomId);
    } catch (err) {
      console.error('[Recording] startRecording 실패:', err);
      dispatch(recordingActions.setStatus('idle'));
      dispatch(toastActions.showSnackbar({
        type: 'error',
        message: '녹화 시작에 실패했습니다. 잠시 후 다시 시도해주세요.',
      }));
    }
  }, [dispatch, startRecordingPolling]);

  const handleRequestFloor = useCallback(() => {
    conferenceManagerRef.current?.sendFloorRequest();
  }, []);

  const handleGrantFloor = useCallback((userId: string) => {
    conferenceManagerRef.current?.sendFloorGrant(userId);
  }, []);

  const handleGrantFloorTarget = useCallback((userId: string, type: 'accept' | 'reject') => {
    conferenceManagerRef.current?.sendFloorGrantTarget(userId, type);
  }, []);

  const handleStopFloor = useCallback(() => {
    conferenceManagerRef.current?.sendFloorStop(localUserId);
  }, [localUserId]);

  const handleChangeDevice = useCallback(async (type: string, deviceId: string) => {
    const m = conferenceManagerRef.current;
    if (!m) return;

    try {
      if (type === 'audioinput') {
        await m.createAndReplaceAudioTrack(deviceId);
      } else if (type === 'videoinput') {
        dispatch(localActions.setVideoInputDeviceId(deviceId));
        // 카메라가 켜져있을 때만 트랙을 교체. OFF 상태에서는 deviceId만 저장하고
        // 이후 카메라 ON 시 저장된 deviceId로 트랙 생성
        if (!store.getState().local.videoMuted) {
          await m.createAndReplaceVideoTrack(deviceId);
        }
      } else if (type === 'audiooutput') {
        await m.setAudioOutputDevice(deviceId);
        dispatch(localActions.setAudioOutputDeviceId(deviceId));
      }
    } catch (err) {
      console.warn('[VideoRenewalPage] device change failed:', type, err);
      dispatch(uiActions.showAlert({ type: 'error', message: '디바이스 변경에 실패했습니다.' }));
    }
  }, [dispatch]);

  // ─── 화이트보드(Excalidraw) 핸들러 — Phase 7에서 구현 ─────────────────────
  // TODO: startWhiteboardSession, stopWhiteboardSession, handleFileUploadForDocumentShare
  // ─── 마스터 측 입장 대기자 감지 (자체 완결형: masterSlice + notificationSlice 직접 디스패치) ──
  const { handleResponseJoinRequest } = useConferenceInit();

  // ─── roomStatus 기반 라우팅 ────────────────────────────────────────────

  if (uiRoomStatus === 'NOT_FOUND' || uiRoomStatus === 'EXPIRED') {
    return <RoomErrorPage type={uiRoomStatus} />;
  }

  // v1 추가 상태 페이지
  if (uiRoomStatus === 'PARTICIPANT_LIMITED') {
    return <RoomErrorPage type="PARTICIPANT_LIMITED" />;
  }

  if (uiRoomStatus === 'CONFERENCE_REFUSAL') {
    return <RoomErrorPage type="CONFERENCE_REFUSAL" />;
  }

  return (
    <>
      <VideoRenewalView
        localUserId={localJitsiId}
        onJoinRoom={handleJoinRoom}
        onToggleAudio={handleToggleAudio}
        onToggleVideo={handleToggleVideo}
        onToggleScreenShare={handleToggleScreenShare}
        onHangup={handleHangup}
        onSendMessage={handleSendMessage}
        onKickParticipant={handleKickParticipant}
        onGrantMaster={handleGrantMaster}
        onClearMaster={handleClearMaster}
        onSelectParticipant={handleSelectParticipant}
        onRequestFloor={handleRequestFloor}
        onGrantFloor={handleGrantFloor}
        onGrantFloorTarget={handleGrantFloorTarget}
        onStopFloor={handleStopFloor}
        onToggleRecording={handleToggleRecording}
        onChangeDevice={handleChangeDevice}
        onResponseJoinRequest={handleResponseJoinRequest}
        onEndMeetingForAll={handleEndMeetingForAll}
      />
      {isExiting && (
        <div className="rtcCustoms_exitOverlay">
          <div className="rtcCustoms_exitOverlay__spinner" />
        </div>
      )}
    </>
  );
};

// ─── 외부 Wrapper (TrackProvider 포함) ───────────────────────────────────────

const VideoRenewalPage: React.FC = () => {
  return (
		<RenewalDialogProvider className="rtcCustoms_fullHeight">
      <TrackProvider>
        <VideoRenewalPageInner />
      </TrackProvider>
    </RenewalDialogProvider>
  );
};

export default VideoRenewalPage;
